import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import SendReport from './sendReport'
import { v3_production_form_report_download_url, v3_production_form_report_preview_url } from '../../../actions/v3/productionForm'
import constant from '../../../constant'
import { hasPermission } from '../../../utils/hasPermission'

/**
 * Server-side report actions for a submitted Production form: PDF/DOC download,
 * inline preview and send-report mail. Renders as <li> items so it drops into the
 * existing `ul.list-inline` action cell of any Submitted form list.
 *
 * The report is generated by /v3/production-form/report/* keyed on
 * (formCode, formId) — no client-side rasterizing, so the PDF text is selectable.
 */
const ReportActions = ({ item, formCode, formName, formId: formIdProp }) => {
    const [showPreview, setShowPreview] = useState(false)
    // The server renders the PDF on request (puppeteer), so the iframe stays blank for
    // a few seconds. Cover it until onLoad fires rather than showing white nothing.
    const [previewLoading, setPreviewLoading] = useState(false)
    // The submitted-list queries are inconsistent: some select submission.ID, others
    // alias it as FORM_ID. Either is the id written into iship_production_form_logs.
    // The consolidated Reports list is the exception - it selects the log row itself,
    // where ID is the log's own key and FORM_ID the submission - so it passes formId.
    const formId = formIdProp ?? item?.ID ?? item?.FORM_ID
    const previewId = `reportPreview-${formCode}-${formId}`
    const mailId = `sendMail-${formCode}-${formId}`
    const canExport = hasPermission(constant.actionPermission.productionExportReport)

    if (!formId) return null

    return (
        <React.Fragment>
            {/* Two plain links rather than an Export dropdown: the action cell lives
                inside .table-responsive, whose overflow clips any popup menu. */}
            <li className="list-inline-item px-0">
                <a
                    href={v3_production_form_report_download_url(formCode, formId, 'pdf')}
                    title="Download PDF"
                    target="_blank"
                    rel="noreferrer"
                >
                    <i className="bx bxs-file-pdf" />
                </a>
            </li>

            <li className="list-inline-item px-0">
                <a
                    href={v3_production_form_report_download_url(formCode, formId, 'docx')}
                    title="Download DOC"
                    target="_blank"
                    rel="noreferrer"
                >
                    <i className="bx bxs-file-doc" />
                </a>
            </li>

            <li className="list-inline-item px-0">
                <a
                    href="#"
                    title="Preview report"
                    data-bs-toggle="modal"
                    data-bs-target={`#${previewId}`}
                    onClick={(e) => { e.preventDefault(); setPreviewLoading(true); setShowPreview(true) }}
                >
                    <i className="bx bx-show" />
                </a>
            </li>

            {/* Mailing is the only export the API can gate: download/preview carry no
                token by design, so hiding those links would be theatre. */}
            {canExport && (
                <li className="list-inline-item px-0">
                    <a
                        href="#"
                        title="Send report"
                        data-bs-toggle="modal"
                        data-bs-target={`#${mailId}`}
                        onClick={(e) => e.preventDefault()}
                    >
                        <i className="bx bx-mail-send" />
                    </a>
                </li>
            )}

            {/* Both modals are portalled to <body>. The action cell's
                ul.list-inline.font-size-20 sets `font-size: 20px !important`, which the
                mail form would otherwise inherit, and a <div> is not valid inside <ul>. */}
            {createPortal(
                <React.Fragment>
                    <div
                        className="modal fade"
                        id={previewId}
                        data-bs-backdrop="static"
                        data-bs-keyboard="false"
                        tabIndex={-1}
                        role="dialog"
                        aria-hidden="true"
                    >
                        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Preview</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => { setShowPreview(false); setPreviewLoading(false) }}
                                    />
                                </div>
                                <div className="modal-body position-relative" style={{ height: '80vh' }}>
                                    {previewLoading && (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100 bg-white"
                                            style={{ zIndex: 1 }}
                                        >
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading</span>
                                            </div>
                                            <p className="text-muted mt-3 mb-0">Loading preview…</p>
                                        </div>
                                    )}
                                    {showPreview && (
                                        <iframe
                                            src={v3_production_form_report_preview_url(formCode, formId)}
                                            title="Report Preview"
                                            width="100%"
                                            height="100%"
                                            style={{ border: 'none' }}
                                            onLoad={() => setPreviewLoading(false)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {canExport && <div
                        className="modal fade"
                        id={mailId}
                        data-bs-backdrop="static"
                        data-bs-keyboard="false"
                        tabIndex={-1}
                        role="dialog"
                        aria-labelledby={`${mailId}Label`}
                        aria-hidden="true"
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id={`${mailId}Label`}>
                                        Send Report Mail
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                    />
                                </div>
                                <div className="modal-body">
                                    <SendReport
                                        data={item}
                                        formCode={formCode}
                                        formName={formName}
                                        formId={formId}
                                        modalId={mailId}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>}
                </React.Fragment>,
                document.body
            )}
        </React.Fragment>
    )
}

export default ReportActions
